import { Injectable, computed, inject, signal } from '@angular/core';
import type { ProjectUiModel } from '../../../core/types';
import { I18nService } from '../../../core';
import { ProjectsStore } from '../stores/projects.store';
import { WorkflowsStore } from '../../workflows/stores/workflows.store';
import type {
  ProjectDetailsViewModel,
  ProjectDetailsWorkflowViewModel,
} from '../models/project-details.model';
import type { ProjectListItemModel, ProjectListMetricPill } from '../models/project-list-item.model';
import { WorkflowItemsService } from '../../workflows/services/workflow-items.service';

@Injectable({ providedIn: 'root' })
export class ProjectsFacade {
  private readonly i18n = inject(I18nService);
  private readonly projectsStore = inject(ProjectsStore);
  private readonly workflowsStore = inject(WorkflowsStore);
  private readonly workflowItemsService = inject(WorkflowItemsService);
  private readonly t = this.i18n.t.bind(this.i18n);

  private readonly querySignal = signal('');
  readonly query = this.querySignal.asReadonly();

  readonly loading = computed(() => this.projectsStore.loading() || this.workflowsStore.loading());
  readonly error = computed(() => this.projectsStore.error() ?? this.workflowsStore.error());
  readonly projects = computed<ProjectListItemModel[]>(() => {
    const workflowsByProject = new Map<string, number>();
    const workflowNamesByProject = new Map<string, string[]>();

    for (const workflow of this.workflowsStore.workflows()) {
      workflowsByProject.set(workflow.projectId, (workflowsByProject.get(workflow.projectId) ?? 0) + 1);
      const names = workflowNamesByProject.get(workflow.projectId) ?? [];
      names.push(workflow.name?.trim() || workflow.id);
      workflowNamesByProject.set(workflow.projectId, names);
    }

    return this.projectsStore.projects().map((project) => {
      const workflowCount = workflowsByProject.get(project.id) ?? 0;
      const totalCardsCount = this.totalCardsForProject(project.id);
      const accentColor = this.resolveProjectAccent(project.name, project.colorHex);

      return {
        ...project,
        workflowCount,
        totalCardsCount,
        lastActivity: this.getProjectLastActivityForCardCount(totalCardsCount),
        activityRank: this.getProjectActivityRank(totalCardsCount),
        categoryLabel: this.getProjectCategoryLabel(project.iconKey),
        statusLabel: totalCardsCount > 0 ? this.t('projects.card.statusactive') : null,
        accentColor,
        accentSurface: `color-mix(in srgb, ${accentColor} 16%, white)`,
        accentForeground: this.resolveAccentForeground(accentColor),
        metricPills: this.buildMetricPills(project, workflowNamesByProject.get(project.id) ?? []),
        metricPillsOverflow: Math.max(0, workflowCount - 4),
      };
    });
  });

  readonly filteredProjects = computed<ProjectListItemModel[]>(() => {
    const query = this.querySignal().trim().toLowerCase();
    const projects = !query
      ? this.projects()
      : this.projects().filter((project) => project.name.toLowerCase().includes(query));

    return [...projects].sort((left, right) => {
      if (right.activityRank !== left.activityRank) return right.activityRank - left.activityRank;
      if (right.totalCardsCount !== left.totalCardsCount) return right.totalCardsCount - left.totalCardsCount;
      if (right.workflowCount !== left.workflowCount) return right.workflowCount - left.workflowCount;
      return left.name.localeCompare(right.name);
    });
  });

  readonly isEmpty = computed(
    () => !this.loading() && !this.error() && this.filteredProjects().length === 0
  );

  setQuery(value: string): void {
    this.querySignal.set(value);
  }

  clearQuery(): void {
    this.querySignal.set('');
  }

  retry(): void {
    this.projectsStore.load();
    this.workflowsStore.load();
  }

  getProjectById(projectId: string | null): ProjectUiModel | null {
    return this.projectsStore.getById(projectId);
  }

  getProjectDetailsById(projectId: string | null): ProjectDetailsViewModel | null {
    const project = this.projectsStore.getById(projectId);
    if (!project) return null;

    const workflows = this.workflowsStore.getByProject(project.id);
    for (const workflow of workflows) {
      void this.workflowItemsService.ensureWorkflowLoaded(workflow.id);
    }

    const workflowViewModels: ProjectDetailsWorkflowViewModel[] = workflows.map((workflow) => {
      const cardsCount = this.workflowItemsService.getByWorkflow(workflow.id).length;
      return {
        id: workflow.id,
        name: workflow.name?.trim() || workflow.id || this.t('domain.workflow'),
        iconKey: workflow.iconKey || 'columns-3',
        colorHex: workflow.colorHex || '#3B82F6',
        stagesCount: workflow.stages.length,
        cardsCount,
        lastActivity: this.getWorkflowLastActivity(workflow.id),
      };
    });

    const totalCardsCount = workflowViewModels.reduce((sum, workflow) => sum + workflow.cardsCount, 0);

    return {
      ...project,
      workflowsCount: workflowViewModels.length,
      totalCardsCount,
      lastActivity: this.getProjectLastActivityForCardCount(totalCardsCount),
      workflows: workflowViewModels,
    };
  }

  private totalCardsForProject(projectId: string): number {
    return this.workflowsStore
      .getByProject(projectId)
      .reduce((sum, workflow) => sum + this.workflowItemsService.getByWorkflow(workflow.id).length, 0);
  }

  private getProjectLastActivityForCardCount(count: number): string {
    if (count === 0) return this.t('activity.nonerecent');
    if (count === 1) return this.t('activity.minutes20');
    if (count <= 3) return this.t('activity.hour1');
    if (count <= 8) return this.t('activity.hours3');
    return this.t('activity.updatedtoday');
  }

  private getWorkflowLastActivity(workflowId: string): string {
    const cardsCount = this.workflowItemsService.getByWorkflow(workflowId).length;
    if (cardsCount === 0) return this.t('activity.none');
    if (cardsCount <= 2) return this.t('activity.hours3');
    if (cardsCount <= 5) return this.t('activity.hour1');
    return this.t('activity.minutes20');
  }

  private getProjectActivityRank(count: number): number {
    if (count === 0) return 0;
    if (count === 1) return 4;
    if (count <= 3) return 3;
    if (count <= 8) return 2;
    return 1;
  }

  private getProjectCategoryLabel(iconKey: string | null | undefined): string {
    const normalized = (iconKey ?? '').trim().toLowerCase();
    if (!normalized || normalized === 'folder') return this.t('projects.card.categorydefault');
    return normalized.replace(/[-_]+/g, ' ');
  }

  private resolveProjectAccent(name: string, colorHex: string | null | undefined): string {
    const normalized = (colorHex ?? '').trim();
    if (/^#([0-9a-f]{6})$/i.test(normalized)) return normalized;

    const palette = ['#262626', '#5B4AD1', '#2F7A59', '#119E51', '#B7791F', '#295A94'];
    let hash = 0;
    for (const char of name) {
      hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    }

    return palette[hash % palette.length] ?? palette[0];
  }

  private resolveAccentForeground(colorHex: string): string {
    const normalized = colorHex.replace('#', '');
    const red = Number.parseInt(normalized.slice(0, 2), 16);
    const green = Number.parseInt(normalized.slice(2, 4), 16);
    const blue = Number.parseInt(normalized.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return luminance > 150 ? '#111827' : '#FFFFFF';
  }

  private buildMetricPills(project: ProjectUiModel, workflowNames: string[]): ProjectListMetricPill[] {
    const sources = [project.name, ...workflowNames, project.description ?? '']
      .flatMap((value) => value.split(/[\s,./|]+/))
      .map((value) => value.trim())
      .filter((value) => value.length >= 2);

    const pills: ProjectListMetricPill[] = [];
    const used = new Set<string>();

    for (const source of sources) {
      const initials = source.slice(0, 2).toUpperCase();
      if (used.has(initials)) continue;
      used.add(initials);
      pills.push({
        initials,
        color: this.resolveProjectAccent(source, null),
      });

      if (pills.length === 4) break;
    }

    if (!pills.length) {
      pills.push({
        initials: project.initials.slice(0, 2).toUpperCase(),
        color: this.resolveProjectAccent(project.name, project.colorHex),
      });
    }

    return pills;
  }
}
